import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import ListBoxSelectorWithSearch from '@product-framework/components/ListBoxSelectorWithSearch';
import UnPaginatedList from '@product-framework/components/UnPaginatedList';

function BrandsSelector({
  onChange = () => undefined,
  serviceProviderId,
  selectedIndustry = {},
  permissions,
}) {
  const leftNameKey = 'brandName';
  const leftValueKey = 'id';
  const rightValueKey = 'id';
  const limit = 10;

  const { formatMessage } = useLocalization();
  const rightTitle = formatMessage({
    id: 'EXCLUDED_FROM_SERVICE',
    defaultMessage: 'Excluded from service',
  });

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';

  const { get, post, remove, getApiUrl } = useDataProvider(provider);
  const snackbar = useSnackbar();

  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(permissions?.brandId, '');

  const getLeftItems = async (page, searchParams) => {
    let searchFilter = '';
    if (searchParams) {
      searchFilter = `&brandName=$like:${searchParams}`;
    }
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/brands?industry.id=$eq:${
        selectedIndustry.value
      }&page=${page}&limit=${limit}${searchFilter}`,
    );

    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
        })) || [];
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = async page => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/excluded-brands?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item,
        })) || [];
      onChange(resultsData);
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const onItemsAdded = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await post(
          `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/excluded-brands`,
          {
            brandId: item,
          },
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEPROVIDER_SKILLS_BRANDS_SAVE_SUCCESS',
            defaultMessage: 'Service Provider brands were saved successfully',
          }),
        );
      }
    }
  };

  const onItemsRemoved = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await remove(
          `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/excluded-brands/${
            item[rightValueKey]
          }`,
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEPROVIDER_SKILLS_BRANDS_REMOVE_SUCCESS',
            defaultMessage: 'Service Provider brands were removed successfully',
          }),
        );
      }
    }
  };

  if (readOnly) {
    return <UnPaginatedList title={rightTitle} getItems={getRightItems} />;
  }

  return (
    <ListBoxSelectorWithSearch
      height="465px"
      getLeftItems={getLeftItems}
      getRightItems={getRightItems}
      parentId={selectedIndustry?.value || null}
      onChange={onChange}
      onItemsAdded={onItemsAdded}
      onItemsRemoved={onItemsRemoved}
      leftTitle={`${selectedIndustry?.label} Brands` || ''}
      rightTitle={rightTitle}
      searchPlaceholder={formatMessage({
        id: 'SEARCH_BRANDS_PLACEHOLDER',
        defaultMessage: 'Search Brands',
      })}
    />
  );
}

export default BrandsSelector;
